import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Calendar, FileText, Plane, Clock, Book, MapPin, Mail, Users } from "lucide-react";
import { toast } from "react-toastify";
import axios from "axios";
import EditTravelDialog from "./EditTravelDialog";
import UserSelectionModal from "../training/UserSelectionModal";

const API_BASE_URL = `${process.env.REACT_APP_API_URL?.replace(/\/$/, "") || ""}/api`;

const UserDayTravelView = ({ user, date, travelEvents, workPermits, trainings, companyId, onClose, fetchData }) => {
  const [travelDetails, setTravelDetails] = useState([]);
  const [workPermitDetails, setWorkPermitDetails] = useState([]);
  const [trainingDetails, setTrainingDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showScheduleTravel, setShowScheduleTravel] = useState(false);
  const [editingTravel, setEditingTravel] = useState(null);
  const [scheduleTravelData, setScheduleTravelData] = useState(null);
  const [selectedTraining, setSelectedTraining] = useState(null);

  useEffect(() => {
    const filteredTravel = travelEvents.filter((item) => {
      const itemDate = new Date(item.start_date || item.planned_date_start);
      return itemDate.toDateString() === date.toDateString() && item.userId === user.username;
    });
    const filteredPermits = workPermits.filter((item) => {
      const itemDate = new Date(item.start_date);
      return itemDate.toDateString() === date.toDateString() && item.userId === user.username;
    });
    const filteredTrainings = trainings.filter((item) => {
      const itemDate = new Date(item.start_date || item.planned_date_start);
      return itemDate.toDateString() === date.toDateString() && item.userId === user.username;
    });
    setTravelDetails(filteredTravel);
    setWorkPermitDetails(filteredPermits);
    setTrainingDetails(filteredTrainings);
    setLoading(false);
  }, [user, date, travelEvents, workPermits, trainings]);

  const prepareTrainingTravelData = (training) => {
    console.log("prepareTrainingTravelData - full training object:", training);
    const startDate = new Date(training.start_date || training.planned_date_start);
    const endDate = new Date(training.end_date || training.planned_date_end);
    
    // Przygotuj pełny opis z informacjami kontaktowymi
    let description = `Training: ${training.trainingName || training.description || "Training"}`;
    if (training.location) {
      description += `\nLocation: ${training.location}`;
    }
    if (training.training_center) {
      if (training.training_center.contact_person) {
        description += `\nContact: ${training.training_center.contact_person}`;
      }
      if (training.training_center.phone) {
        description += `\nPhone: ${training.training_center.phone}`;
      }
      if (training.training_center.email) {
        description += `\nEmail: ${training.training_center.email}`;
      }
    }
    
    // Log the training structure to debug
    console.log("training.required_items:", training.required_items);
    console.log("training.training:", training.training);
    console.log("training.training?.required_items:", training.training?.required_items);
    
    return {
      address: training.address || (training.training_center?.address) || training.location || "Training Center",
      startTime: startDate.toTimeString().slice(0, 5),
      endTime: endDate.toTimeString().slice(0, 5),
      description: description,
      required_items: training.required_items || training.training?.required_items || ''
    };
  };

  const handleScheduleTravelFromTraining = (training) => {
    const travelData = prepareTrainingTravelData(training);
    setScheduleTravelData(travelData);
    setSelectedTraining(training);
    setShowScheduleTravel(true);
  };

  const handleSendMultipleEmails = async (formData, travelDate, travelObjects, selectedUserObjects) => {
    try {
      const token = localStorage.getItem("access_token");
      const travelDateObj = travelDate || date;
      
      // Include the original user (current user) in the email list
      const allUsersForEmail = [...selectedUserObjects, user];
      // Remove duplicates based on user ID
      const uniqueUsersForEmail = allUsersForEmail.filter((userObj, index, arr) => 
        arr.findIndex(u => u.id === userObj.id) === index
      );
      
      // Send email to each user with their corresponding travel object
      const emailPromises = uniqueUsersForEmail.map((userObj, index) => {
        const travelObj = travelObjects[index];
        
        const emailData = {
          user_email: userObj.email,
          user_name: `${userObj.first_name} ${userObj.last_name}`,
          travel_id: travelObj.id,
          travel_data: {
            trainingName: scheduleTravelData?.description?.split('\n')[0]?.replace('Training: ', '') || formData.description,
            address: formData.address,
            date: new Date(date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }),
            travelDate: new Date(travelDateObj).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }),
            startTime: formData.startTime,
            endTime: formData.endTime,
            description: formData.description,
            required_items: scheduleTravelData?.required_items || selectedTraining?.required_items || formData.required_items || ''
          }
        };

        return axios.post(`${API_BASE_URL}/travel/send_travel_email/`, emailData, {
          headers: { Authorization: `Bearer ${token}` },
        });
      });

      await Promise.all(emailPromises);
      toast.success(`Travel emails sent to ${uniqueUsersForEmail.length} users`);
      
      // Wait a moment for backend to update the email_sent flag
      setTimeout(() => {
        fetchData();
      }, 500);
    } catch (error) {
      console.error("Error sending multiple emails:", error);
      toast.error("Error sending travel emails");
    }
  };

  const handleSendTravelEmail = async (formData, travelDate, travelId = null, trainingData = null) => {
    try {
      const token = localStorage.getItem("access_token");
      const travelDateObj = travelDate || date;
      
      console.log("handleSendTravelEmail - scheduleTravelData:", scheduleTravelData);
      console.log("handleSendTravelEmail - trainingData:", trainingData);
      console.log("handleSendTravelEmail - formData:", formData);
      
      const emailData = {
        user_email: user.email,
        user_name: `${user.first_name} ${user.last_name}`,
        travel_id: travelId,
        travel_data: {
          trainingName: scheduleTravelData?.description?.split('\n')[0]?.replace('Training: ', '') || formData.description,
          address: formData.address,
          date: new Date(date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }),
          travelDate: new Date(travelDateObj).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }),
          startTime: formData.startTime,
          endTime: formData.endTime,
          description: formData.description,
          required_items: scheduleTravelData?.required_items || trainingData?.required_items || formData.required_items || ''
        }
      };

      await axios.post(`${API_BASE_URL}/travel/send_travel_email/`, emailData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success("Email sent successfully");
      
      // Wait a moment for backend to update the email_sent flag
      setTimeout(() => {
        fetchData();
      }, 500);
    } catch (error) {
      console.error("Error sending email:", error);
      toast.error("Error sending email");
    }
  };

  const handleScheduleTravel = async (travelFormData, travelDate = date, userIds = null) => {
    try {
      const token = localStorage.getItem("access_token");
      const startDate = new Date(travelDate);
      const [startHours, startMinutes] = travelFormData.startTime.split(":");
      startDate.setHours(parseInt(startHours), parseInt(startMinutes), 0, 0);
      const endDate = new Date(travelDate);
      const [endHours, endMinutes] = travelFormData.endTime.split(":");
      endDate.setHours(parseInt(endHours), parseInt(endMinutes), 0, 0);

      // If userIds are provided, it's a multi-user scheduling
      if (userIds && userIds.length > 0) {
        // Include the original user (the one clicked in calendar) plus selected users
        const allUserIds = [...userIds, user.id];
        // Remove duplicates in case the original user was also selected in modal
        const uniqueUserIds = [...new Set(allUserIds)];
        
        console.log("Multi-user travel scheduling:");
        console.log("Selected userIds:", userIds);
        console.log("Original user.id:", user.id);
        console.log("All userIds:", allUserIds);
        console.log("Unique userIds:", uniqueUserIds);
        
        // Create travel assignments for all users (including original)
        const promises = uniqueUserIds.map(userId => {
          const formData = new FormData();
          formData.append("user_id", userId);
          formData.append("start_date", startDate.toISOString());
          formData.append("end_date", endDate.toISOString());
          formData.append("company", companyId);
          formData.append("address", travelFormData.address);
          formData.append("description", travelFormData.description || travelFormData.address);
          formData.append("required_items", scheduleTravelData?.required_items || selectedTraining?.required_items || "");
          if (travelFormData.ferryAttachment) formData.append("attachment_ferry", travelFormData.ferryAttachment);
          if (travelFormData.flightAttachment) formData.append("attachment_flight", travelFormData.flightAttachment);

          return axios.post(`${API_BASE_URL}/travel/`, formData, {
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
          });
        });

        const results = await Promise.all(promises);
        toast.success(`Travel has been scheduled for ${uniqueUserIds.length} users`);
        setShowScheduleTravel(false);
        setScheduleTravelData(null);
        fetchData();
        return results.map(result => result.data); // Return all created travel objects
      } else {
        // Single user scheduling (original behavior)
        const formData = new FormData();
        formData.append("user_id", user.id);
        formData.append("start_date", startDate.toISOString());
        formData.append("end_date", endDate.toISOString());
        formData.append("company", companyId);
        formData.append("address", travelFormData.address);
        formData.append("description", travelFormData.description || travelFormData.address);
        formData.append("required_items", scheduleTravelData?.required_items || selectedTraining?.required_items || "");
        if (travelFormData.ferryAttachment) formData.append("attachment_ferry", travelFormData.ferryAttachment);
        if (travelFormData.flightAttachment) formData.append("attachment_flight", travelFormData.flightAttachment);

        const response = await axios.post(`${API_BASE_URL}/travel/`, formData, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
        });

        toast.success("Travel has been scheduled");
        setShowScheduleTravel(false);
        setScheduleTravelData(null);
        fetchData();
        
        return response.data; // Return the created travel object
      }
    } catch (error) {
      console.error("Error scheduling travel:", error);
      toast.error("Error scheduling travel");
      throw error;
    }
  };

  const TravelScheduleForm = ({ defaultData = null }) => {
    const [formData, setFormData] = useState({
      address: defaultData?.address || "",
      startTime: defaultData?.startTime || "08:00",
      endTime: defaultData?.endTime || "18:00",
      description: defaultData?.description || "",
      ferryAttachment: null,
      flightAttachment: null,
    });
    const [travelDayBefore, setTravelDayBefore] = useState(false);
    const [sendEmail, setSendEmail] = useState(false);
    const [showUserSelectionModal, setShowUserSelectionModal] = useState(false);
    const [selectedUserIds, setSelectedUserIds] = useState([]);
    const [selectedUserObjects, setSelectedUserObjects] = useState([]);

    const handleChange = (field) => (e) => {
      const value = e.target.type === "file" ? e.target.files[0] : e.target.value;
      setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async (userIds = null, selectedUserObjects = null) => {
      try {
        const travelDate = travelDayBefore ? new Date(date.getTime() - 24 * 60 * 60 * 1000) : date;
        const createdTravel = await handleScheduleTravel(formData, travelDate, userIds);
        
        if (sendEmail && createdTravel) {
          if (userIds && selectedUserObjects) {
            // Multi-user emails - createdTravel is array of travel objects
            await handleSendMultipleEmails(formData, travelDate, createdTravel, selectedUserObjects);
          } else if (createdTravel.id) {
            // Single user email - createdTravel is single object
            await handleSendTravelEmail(formData, travelDate, createdTravel.id, selectedTraining);
          }
        }
        
        setFormData({
          address: "",
          startTime: "08:00",
          endTime: "18:00",
          description: "",
          ferryAttachment: null,
          flightAttachment: null,
        });
        setTravelDayBefore(false);
        setSendEmail(false);
        setSelectedUserIds([]);
        setSelectedUserObjects([]);
      } catch (error) {
        // Error already handled in handleScheduleTravel
      }
    };

    return (
      <div className="mt-4 p-4 border border-green-200 rounded-lg bg-green-50">
        <h3 className="text-sm font-medium text-green-800 mb-3 flex items-center">
          <Plane className="h-4 w-4 mr-2" />
          Schedule Travel
        </h3>
        <div className="space-y-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Destination Address</label>
            <textarea
              className="w-full p-2 text-sm border rounded"
              value={formData.address}
              onChange={handleChange("address")}
              placeholder="Enter destination address"
              rows={2}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Start Time</label>
              <input
                type="time"
                className="w-full p-2 text-sm border rounded"
                value={formData.startTime}
                onChange={handleChange("startTime")}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">End Time</label>
              <input
                type="time"
                className="w-full p-2 text-sm border rounded"
                value={formData.endTime}
                onChange={handleChange("endTime")}
              />
            </div>
          </div>
          {defaultData && (
            <div className="flex items-center space-x-2 bg-blue-50 p-2 rounded border border-blue-200">
              <input
                type="checkbox"
                id="travelDayBefore"
                checked={travelDayBefore}
                onChange={(e) => setTravelDayBefore(e.target.checked)}
                className="rounded border-gray-300"
              />
              <label htmlFor="travelDayBefore" className="text-sm text-blue-800 flex items-center">
                <Calendar className="h-3 w-3 mr-1" />
                Schedule travel day before training
                <span className="ml-1 text-xs text-gray-600">
                  ({travelDayBefore ? new Date(date.getTime() - 24 * 60 * 60 * 1000).toLocaleDateString() : new Date(date).toLocaleDateString()})
                </span>
              </label>
            </div>
          )}
          <div>
            <label className="block text-xs text-gray-600 mb-1">Description</label>
            <textarea
              className="w-full p-2 text-sm border rounded"
              value={formData.description}
              onChange={handleChange("description")}
              placeholder="Enter travel details"
              rows={3}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Ferry Attachment</label>
              <input
                type="file"
                className="w-full p-1 text-xs border rounded"
                onChange={handleChange("ferryAttachment")}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Flight Attachment</label>
              <input
                type="file"
                className="w-full p-1 text-xs border rounded"
                onChange={handleChange("flightAttachment")}
              />
            </div>
          </div>
          <div className="flex items-center space-x-2 bg-green-50 p-2 rounded border border-green-200">
            <input
              type="checkbox"
              id="sendEmail"
              checked={sendEmail}
              onChange={(e) => setSendEmail(e.target.checked)}
              className="rounded border-gray-300"
            />
            <label htmlFor="sendEmail" className="text-sm text-green-800 flex items-center">
              <Mail className="h-3 w-3 mr-1" />
              {selectedUserIds.length > 0 
                ? `Send travel information emails to ${selectedUserIds.length + 1} users` 
                : `Send travel information email to ${user.first_name}`
              }
            </label>
          </div>
          {/* Action buttons */}
          <div className="flex justify-between items-center pt-3 border-t">
            <div>
              {selectedUserIds.length > 0 && (
                <div className="text-sm text-green-600">
                  {selectedUserIds.length + 1} users selected (including current)
                </div>
              )}
            </div>
            <div className="flex space-x-2">
              <Button variant="outline" size="sm" onClick={() => setShowScheduleTravel(false)}>
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={() => setShowUserSelectionModal(true)}
                disabled={!formData.address}
                className={`${!formData.address ? "opacity-50" : ""} flex items-center`}
              >
                <Users className="h-4 w-4 mr-1" />
                {selectedUserIds.length > 0 ? 'Change Users' : 'Select Multiple Users'}
              </Button>
              <Button
                size="sm"
                onClick={() => handleSubmit(selectedUserIds.length > 0 ? selectedUserIds : null, selectedUserObjects.length > 0 ? selectedUserObjects : null)}
                disabled={!formData.address}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                Schedule Travel
              </Button>
            </div>
          </div>
        </div>
        
        {/* User Selection Modal */}
        <UserSelectionModal
          isOpen={showUserSelectionModal}
          onClose={() => setShowUserSelectionModal(false)}
          onConfirm={(selectedUserObjects) => {
            const userIds = selectedUserObjects.map(user => user.id);
            setSelectedUserIds(userIds);
            setSelectedUserObjects(selectedUserObjects);
            setShowUserSelectionModal(false);
          }}
          currentUserId={user?.id}
          trainingFilter={selectedTraining ? {
            trainingName: selectedTraining.trainingName || selectedTraining.description,
            date: selectedTraining.start_date || selectedTraining.planned_date_start
          } : null}
          returnFullObjects={true}
        />
      </div>
    );
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Calendar className="h-5 w-5 text-green-600" />
            <div>
              <span className="font-bold text-green-600">{user.first_name} {user.last_name}</span>
              <span className="text-gray-600"> - </span>
              <span className="text-gray-600 font-medium">
                {new Date(date).toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
              </span>
            </div>
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="flex flex-col items-center justify-center py-8">
            <p className="text-gray-500 text-sm">Loading data...</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-sm font-semibold text-gray-700 flex items-center">
                <FileText className="h-4 w-4 mr-2" />
                Events for this day
              </h3>
            </div>
            {showScheduleTravel && <TravelScheduleForm defaultData={scheduleTravelData} />}
            {(travelDetails.length > 0 || workPermitDetails.length > 0 || trainingDetails.length > 0) ? (
              <div className="space-y-3 max-h-[250px] overflow-y-auto pr-2">
                {travelDetails.map((item) => (
                  <div
                    key={item.id}
                    className="bg-green-100 border-green-300 border rounded-lg p-3 shadow-sm relative overflow-hidden"
                  >
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-green-500"></div>
                    <div className="pl-2">
                      <div className="font-medium text-sm flex items-center justify-between">
                        <div className="flex items-center">
                          <Plane className="h-4 w-4 mr-2 text-green-600" />
                          {item.address || item.description || "Unnamed travel"}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={() => setEditingTravel(item)}
                        >
                          <Plane className="h-3 w-3 text-green-600" />
                        </Button>
                      </div>
                      <div className="text-xs text-gray-500 mt-1 flex items-center">
                        <Clock className="h-3 w-3 mr-1" />
                        {new Date(item.planned_date_start || item.start_date).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false })}
                        {" - "}
                        {new Date(item.planned_date_end || item.end_date).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false })}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">{item.location || "No location"}</div>
                      {(item.comments || item.description) && (
                        <div className="mt-2 text-xs text-gray-600 bg-gray-50 p-2 rounded border">
                          {item.comments || item.description}
                        </div>
                      )}
                      {item.email_sent && (
                        <div className="mt-2 flex items-center text-xs text-green-700 bg-green-50 p-2 rounded">
                          <Mail className="h-3 w-3 mr-1" />
                          Email sent {item.email_sent_at ? new Date(item.email_sent_at).toLocaleDateString() : ''}
                        </div>
                      )}
                      {(item.attachment_ferry || item.attachment_flight) && (
                        <div className="mt-3 space-y-2">
                          <h4 className="font-medium text-xs">Travel Attachments</h4>
                          <div className="grid grid-cols-2 gap-2">
                            {item.attachment_ferry && (
                              <a
                                href={item.attachment_ferry}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center p-2 bg-green-50 rounded border border-green-200 text-green-700 text-xs"
                              >
                                <FileText className="h-3 w-3 mr-1" />
                                Ferry Ticket
                              </a>
                            )}
                            {item.attachment_flight && (
                              <a
                                href={item.attachment_flight}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center p-2 bg-green-50 rounded border border-green-200 text-green-700 text-xs"
                              >
                                <FileText className="h-3 w-3 mr-1" />
                                Flight Ticket
                              </a>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {workPermitDetails.map((item) => (
                  <div
                    key={item.id}
                    className="bg-yellow-100 border-yellow-300 border rounded-lg p-3 shadow-sm relative overflow-hidden"
                  >
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-yellow-500"></div>
                    <div className="pl-2">
                      <div className="font-medium text-sm flex items-center">
                        <Plane className="h-4 w-4 mr-2 text-yellow-600" />
                        {item.work_permit_name || "Unnamed permit"}
                      </div>
                      <div className="text-xs text-gray-500 mt-1 flex items-center">
                        <Clock className="h-3 w-3 mr-1" />
                        {new Date(item.start_date).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false })}
                        {" - "}
                        {new Date(item.end_date).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false })}
                      </div>
                    </div>
                  </div>
                ))}
                {trainingDetails.map((item) => (
                  <div
                    key={item.id}
                    className="bg-blue-100 border-blue-300 border rounded-lg p-3 shadow-sm relative overflow-hidden"
                  >
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-blue-500"></div>
                    <div className="pl-2">
                      <div className="font-medium text-sm flex items-center justify-between">
                        <div className="flex items-center">
                          <Book className="h-4 w-4 mr-2 text-blue-600" />
                          {item.trainingName || "Unnamed training"}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-xs bg-green-100 hover:bg-green-200 text-green-700"
                          onClick={() => handleScheduleTravelFromTraining(item)}
                        >
                          <Plane className="h-3 w-3 mr-1" />
                          Schedule Travel
                        </Button>
                      </div>
                      <div className="text-xs text-gray-500 mt-1 flex items-center">
                        <Clock className="h-3 w-3 mr-1" />
                        {new Date(item.planned_date_start || item.start_date).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false })}
                        {" - "}
                        {new Date(item.planned_date_end || item.end_date).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false })}
                      </div>
                      {(item.address || item.location) && (
                        <div className="text-xs text-gray-500 mt-1 flex items-center">
                          <MapPin className="h-3 w-3 mr-1" />
                          {item.address || item.location}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 bg-gray-50 rounded-lg">
                <Calendar className="h-10 w-10 text-gray-300 mb-2" />
                <p className="text-gray-500">No events for this day</p>
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
        {editingTravel && (
          <EditTravelDialog
            isOpen={!!editingTravel}
            onClose={() => setEditingTravel(null)}
            travelData={editingTravel}
            fetchData={fetchData}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default UserDayTravelView;